import { Employee, formatDate, parseDateString, type JobPostingDTO } from "@pentacode/core";
import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import "../../../components/avatar/avatar";
import type { Avatar } from "../../../components/avatar/avatar";
import "../../../elements/popover";
import { shared } from "../../../styles";

type JobApplication = JobPostingDTO["applications"][number];

@customElement("ptc-job-application-preview")
export class JobApplicationPreview extends LitElement {
    @property({ attribute: false })
    application: JobApplication;

    @property({ type: Boolean })
    clickable = true;

    private _handleClick() {
        if (this.clickable) {
            this.dispatchEvent(
                new CustomEvent("application-click", {
                    detail: { application: this.application },
                    bubbles: true,
                    composed: true,
                })
            );
        }
    }

    static styles = [
        shared,
        css`
            :host {
                display: block;
            }

            .preview {
                display: block;
            }

            .preview.clickable {
                cursor: pointer;
            }

            .note-indicator {
                color: var(--orange);
                font-size: 1.1em;
            }
            .note-text {
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 6;
                line-clamp: 6;
                line-height: 1.4;
                max-height: calc(6 * 1.4em);
                max-width: 280px;
                overflow: hidden;
            }
        `,
    ];

    render() {
        if (!this.application) {
            return html``;
        }

        const age =
            this.application.birthday &&
            Math.floor(
                (Date.now() - parseDateString(this.application.birthday)!.getTime()) / (24 * 3600 * 365.25 * 1000)
            );
        const avatarEmployee = new Employee({
            avatar: this.application.headShotUrl,
            firstName: this.application.firstName,
            lastName: this.application.lastName,
        }) as Avatar["employee"];

        return html`
            <div
                part="card"
                class="double-padded box preview ${this.clickable ? "click clickable" : ""}"
                @click=${this._handleClick}
            >
                <div class="horizontal start-aligning spacing layout">
                    <ptc-avatar
                        .employee=${avatarEmployee}
                        class="large"
                        .badge=${age ? { text: age.toString() } : undefined}
                    ></ptc-avatar>
                    <div class="stretch">
                        <div class="bold">${this.application.firstName} ${this.application.lastName}</div>
                        <div class="small">Beworben am <strong>${formatDate(this.application.created)}</strong></div>
                        <div class="small">
                            Verfügbar ab
                            <strong>
                                ${this.application.availableFrom
                                    ? formatDate(this.application.availableFrom)
                                    : "sofort"}
                            </strong>
                        </div>
                    </div>
                    ${this.application.notes?.trim()
                        ? html` <ptc-ui-icon
                                  name="icon.object.note:active"
                                  class="note-indicator"
                                  title="Notiz vorhanden"
                                  @click=${(e: Event) => e.stopPropagation()}
                              ></ptc-ui-icon>
                              <ptc-popover class="note-popover" trigger="hover">
                                  <div class="note-text">${this.application.notes}</div>
                              </ptc-popover>`
                        : nothing}
                </div>
            </div>
        `;
    }
}
